import { useState, useEffect, type ChangeEvent } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Field } from '@atlaskit/form';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWorkflowDangerIcon from '@atlaskit/icon-lab/core/status-workflow-danger';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { useAccountCreationContext } from '@/components/signup/account-creation/hooks/account-creation-context';
import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { fontSize } from '@/components/signup/utils/font-size';
import { Button } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';

export const InlineInputSubmit = ({
	helpText,
	darkMode = false,
}: {
	helpText?: string;
	darkMode?: boolean;
}) => {
	const intl = useIntl();
	const {
		isReadyForInvalid,
		setIsEmailValid,
		setIsReadyForInvalid,
		setTypeAheadEmail,
		isSubmitting,
		hasSubmitted,
		formError,
		onClickSubmitButton,
		typeAheadEmail,
	} = useAccountCreationContext();
	const [email, setEmail] = useState(typeAheadEmail);
	const [isValid, setValid] = useState<boolean>();

	const defaultHelpText = intl.formatMessage({
		id: 'signupForm.accountCreation.helperTextTeam26',
		defaultMessage: 'Use a work email to find teammates and get access to Rovo AI',
	});
	const emailHelpText = helpText?.trim() || defaultHelpText;

	const errorMessageText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.invalidEmail',
		defaultMessage: 'Please enter a valid email address.',
	});

	const placeholderText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.workEmail',
		defaultMessage: 'Work email',
	});

	const emailPlaceholder = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.emailPlaceholder',
		defaultMessage: 'you@company.com',
	});

	const validMessage = intl.formatMessage({
		id: 'gwp.accountSignin.validEmailIcon',
		defaultMessage: 'Email is valid',
	});

	const buttonText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.submit',
		defaultMessage: 'Sign up',
	});

	useEffect(() => {
		const valid = emailIsValid(email);

		if (!valid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}

		setValid(valid);
		setIsEmailValid(valid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	return (
		<div css={containerCss}>
			<label css={[labelCss, helpTextContainerCss]}>{placeholderText}</label>
			<div
				css={[
					inputSubmitGroupCss,
					darkMode && darkModeBgCss,
					isValid === undefined && inputSubmitGroupFocusCss,
					isValid === false && invalidBorderCss,
					isValid === true && validBorderCss,
				]}
			>
				<div css={inputWrapperCss}>
					<Field aria-required={true} isRequired label="" name="email">
						{({ fieldProps }) => (
							<div css={inputInnerCss}>
								<TextField
									{...fieldProps}
									autoComplete="email"
									id="inline_email_embedded_signup_input"
									aria-label={emailPlaceholder}
									onBlur={() => setIsReadyForInvalid(true)}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										setEmail(e.target.value);
										setTypeAheadEmail(e.target.value);
										fieldProps.onChange(e);
									}}
									placeholder={emailPlaceholder}
									testId="email_field"
									type="email"
									value={email}
								/>
								{isValid === false ? (
									<div css={[iconCss, invalidIconCss]}>
										<StatusWorkflowDangerIcon label={errorMessageText} />
									</div>
								) : isValid === true ? (
									<div css={[iconCss, validIconCss]}>
										<SuccessIcon label={validMessage} />
									</div>
								) : null}
							</div>
						)}
					</Field>
				</div>
				<Button
					css={buttonCss}
					type="submit"
					disabled={isSubmitting || (hasSubmitted && !formError)}
					onClick={onClickSubmitButton}
					data-testid="embedded-signup-submit"
					dataEventProps={{
						'data-product-key': 'uniform',
						'data-event': 'clicked',
						'data-event-component': 'button',
						'data-event-container': 'signup',
						'data-label': buttonText,
						'data-label-english': 'Sign up',
					}}
				>
					{buttonText}
				</Button>
			</div>
			<div css={helpTextContainerCss}>
				<label css={helpTextCss} htmlFor="inline_email_embedded_signup_input">
					{emailHelpText}
				</label>
				{isValid === false ? (
					<div css={errorMessageCss} role="alert">
						{errorMessageText}
					</div>
				) : null}
			</div>
		</div>
	);
};

const containerCss = css({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: '4px',
});

const labelCss = css({
	color: 'inherit',
	fontFamily: wacToken('font.charlie.text'),
	fontWeight: 500,
	fontSize: '14px',
	marginInlineStart: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
	['[aria-hidden=true]']: {
		display: 'none',
	},
});

const inputSubmitGroupCss = css({
	display: 'flex',
	flexDirection: 'row',
	height: '44px',
	boxSizing: 'border-box',
	alignItems: 'center',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
	borderRadius: 40,
	gap: token('space.150'),
	width: '100%',
	['@media not all and (min-width: 30rem)']: {
		flexDirection: 'column',
		height: 'auto',
		border: 'none',
		backgroundColor: 'transparent',
		borderRadius: 0,
		gap: token('space.200'),
		alignItems: 'stretch',
	},
});

const inputSubmitGroupFocusCss = css({
	['&:focus-within']: {
		boxShadow: `inset 0 0 0 1px ${wacToken('color.extended.blue600')}`,
		borderColor: wacToken('color.extended.blue600'),
	},
});

const inputWrapperCss = css({
	flexGrow: 1,
	minWidth: 0,
	height: '100%',
	['@media not all and (min-width: 30rem)']: {
		height: '44px',
		backgroundColor: wacToken('color.neutral.light0'),
		border: `1px solid ${wacToken('color.neutral.light400')}`,
		borderRadius: 40,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override Field wrapper spacing
	['> div']: {
		margin: 0,
		padding: 0,
		height: '100%',
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['label']: {
		margin: '0 auto',
	},
});

const inputInnerCss = css({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input:-webkit-autofill:focus']: {
		['transition']: 'background-color 5000s ease-in-out 0s',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		height: '100%',
		background: 'transparent',
		color: 'inherit',
		fontFamily: 'inherit',
		fontSize: '1rem', // 16px — matches control (fontSize.md)
		overflow: 'hidden',
		paddingInlineStart: '12px !important',
		textOverflow: 'ellipsis',
		display: 'flex',
		alignItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input::placeholder']: {
		fontFamily: 'inherit',
		fontSize: '1rem',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input:focus']: {
		background: 'transparent !important',
		backgroundColor: 'transparent !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['> div']: {
		backgroundColor: 'transparent !important',
		border: 'none',
		borderRadius: 0,
		boxShadow: 'none !important',
		height: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['span']: {
		alignSelf: 'center',
	},
});

const buttonCss = css({
	alignItems: 'center',
	boxSizing: 'border-box',
	display: 'flex',
	fontFamily: 'inherit',
	fontSize: '1rem', // 16px — matches control
	fontWeight: 400,
	height: '44px',
	justifyContent: 'center',
	minWidth: 'auto',
	whiteSpace: 'nowrap',
	width: 'fit-content',
	margin: '-1px -1px -1px 0',
	['@media not all and (min-width: 30rem)']: {
		width: '100%',
		margin: 0,
	},
});

const helpTextContainerCss = css({
	marginInlineStart: 0,
	marginInlineEnd: 0,
});

const helpTextCss = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '12px',
	lineHeight: 1.5,
	marginBlockStart: 6,
	color: 'inherit !important',
	opacity: 0.8,
});

const errorMessageCss = css({
	color: wacToken('color.secondary.red'),
	fontSize: fontSize.xs,
	lineHeight: 1.5,
});

const darkModeBgCss = css({
	backgroundColor: 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		color: `${wacToken('color.neutral.light0')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input::placeholder']: {
		color: `${wacToken('color.neutral.light0')} !important`,
	},
});

const invalidBorderCss = css({
	borderColor: wacToken('color.secondary.red'),
	boxShadow: `inset 0 0 0 1px ${wacToken('color.secondary.red')}`,
});

const validBorderCss = css({
	borderColor: wacToken('color.extended.lime600'),
	boxShadow: `inset 0 0 0 1px ${wacToken('color.extended.lime600')}`,
});

const iconCss = css({
	display: 'flex',
	alignSelf: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		height: '20px',
		width: '20px',
	},
});

const invalidIconCss = css({
	color: wacToken('color.secondary.red'),
});

const validIconCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		color: wacToken('color.extended.lime600'),
	},
});
